// src/pages/Services.jsx
import React from "react";
import { motion } from "framer-motion";
import {
  Hotel,
  Map,
  Plane,
  Car,
  Camera,
  Briefcase,
  ChevronRight,
  Star,
  Shield,
  Clock,
  Heart,
} from "lucide-react";
import { Link } from "react-router-dom";
import translations from "../contexts/translations";
import zelijPattern from "../assets/zelij3.png";

// ✅ Import existing project images
import riadImg from "../assets/riad.png";
import tourImg from "../assets/tour.png";
import transportImg from "../assets/transport.png";
import carImg from "../assets/car.png";
import guidedImg from "../assets/guided.png";

const safe = (v, fallback) => (v === undefined || v === null || v === "" ? fallback : v);

// Service images mapped to their cards
const serviceImages = {
  1: riadImg,
  2: tourImg,
  3: transportImg,
  4: carImg,
  5: guidedImg,
  6: "https://res.cloudinary.com/dzszitsgb/image/upload/v1773766011/nouzha/destination-tangerrabat-20260317.jpg",
};

// Beautiful gallery images for the destinations strip
const galleryImages = [
  { src: "https://res.cloudinary.com/dzszitsgb/image/upload/v1773760319/nouzha/ubllqf3hfvqox1qixeby.jpg", label: "Marrakech" },
  { src: "https://res.cloudinary.com/dzszitsgb/image/upload/v1773759769/nouzha/sahara-20260317-new.jpg", label: "Sahara" },
  { src: "https://res.cloudinary.com/dzszitsgb/image/upload/v1773766008/nouzha/destination-chefchaoun-20260317.jpg", label: "Chefchaouen" },
  { src: "https://res.cloudinary.com/dzszitsgb/image/upload/v1773768781/nouzha/destination-essaouira-20260317.jpg", label: "Essaouira" },
  { src: "https://res.cloudinary.com/dzszitsgb/image/upload/v1773759766/nouzha/fes-20260317-new.jpg", label: "Fès" },
];

const Services = ({ language }) => {
  const t = translations?.[language] || translations?.fr || {};

  const services = [
    {
      id: 1,
      title: safe(t.riads, "Riads & Hôtels"),
      description: safe(t.riadsDesc, "Riads marocains authentiques et hôtels de luxe dans les villes impériales."),
      icon: <Hotel className="w-6 h-6" />,
      to: "/sejour",
    },
    {
      id: 2,
      title: safe(t.circuits, "Circuits & Tours"),
      description: safe(t.circuitsDesc, "Visites guidées à travers l'Atlas, le Sahara et les médinas anciennes."),
      icon: <Map className="w-6 h-6" />,
      to: "/circuit",
    },
    {
      id: 3,
      title: safe(t.transport, "Transport"),
      description: safe(t.transportDesc, "Transport confortable entre les villes et sites touristiques."),
      icon: <Plane className="w-6 h-6" />,
      to: "/transport",
    },
    {
      id: 4,
      title: safe(t.carRental, "Location de Voiture"),
      description: safe(t.carRentalDesc, "Explorez le Maroc à votre rythme avec nos services de location fiables et abordables."),
      icon: <Car className="w-6 h-6" />,
      to: "/location",
    },
    {
      id: 5,
      title: safe(t.guidedTours, "Visites Guidées"),
      description: safe(t.guidedToursDesc, "Découvrez l'histoire et la culture du Maroc avec nos guides experts."),
      icon: <Camera className="w-6 h-6" />,
      to: "/guided-tours",
    },
    {
      id: 6,
      title: safe(t.professionals, "Espace Professionnels"),
      description: safe(t.professionalsDesc, "Accédez à l'espace Pro pour les partenaires : agences, chauffeurs, guides et prestataires."),
      icon: <Briefcase className="w-6 h-6" />,
      to: "/agency-partnership",
    },
  ];

  const processSteps = [
    {
      title: safe(t.search, "Recherchez"),
      description: safe(t.searchDesc, "Parcourez nos services premium, des Riads aux circuits guidés, grâce à nos filtres intuitifs."),
      icon: <Hotel className="w-6 h-6 text-orange-600" />,
      image: "https://res.cloudinary.com/dzszitsgb/image/upload/v1773760319/nouzha/ubllqf3hfvqox1qixeby.jpg",
    },
    {
      title: safe(t.select, "Sélectionnez"),
      description: safe(t.selectDesc, "Choisissez parmi notre sélection d'expériences marocaines authentiques adaptées à vos envies."),
      icon: <Map className="w-6 h-6 text-orange-600" />,
      image: "https://res.cloudinary.com/dzszitsgb/image/upload/v1773759769/nouzha/sahara-20260317-new.jpg",
    },
    {
      title: safe(t.book, "Réservez"),
      description: safe(t.bookDesc, "Finalisez votre réservation avec notre processus sécurisé et nos options de paiement flexibles."),
      icon: <Plane className="w-6 h-6 text-orange-600" />,
      image: "https://res.cloudinary.com/dzszitsgb/image/upload/v1773766008/nouzha/destination-chefchaoun-20260317.jpg",
    },
    {
      title: safe(t.enjoy, "Profitez"),
      description: safe(t.enjoyDesc, "Vivez la magie du Maroc en toute confiance, assisté par notre équipe locale 7j/7."),
      icon: <Camera className="w-6 h-6 text-orange-600" />,
      image: tourImg,
    },
  ];

  const trustHighlights = [
    {
      title: safe(t.servicesTrustSupportTitle, "Support 7j/7"),
      description: safe(t.servicesTrustSupportDesc, "Équipe locale réactive avant, pendant et après votre voyage."),
      value: safe(t.servicesTrustSupportValue, "< 15 min"),
      icon: <Clock className="w-5 h-5 text-orange-500" />,
    },
    {
      title: safe(t.servicesTrustPaymentTitle, "Paiement sécurisé"),
      description: safe(t.servicesTrustPaymentDesc, "Transactions protégées via passerelles de paiement vérifiées."),
      value: safe(t.servicesTrustPaymentValue, "CMI + Stripe"),
      icon: <Shield className="w-5 h-5 text-orange-500" />,
    },
    {
      title: safe(t.servicesTrustCustomTitle, "Offres sur mesure"),
      description: safe(t.servicesTrustCustomDesc, "Expériences adaptées à votre style, votre rythme et votre budget."),
      value: safe(t.servicesTrustCustomValue, "100% flexible"),
      icon: <Heart className="w-5 h-5 text-orange-500" />,
    },
  ];

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "#FFF7ED" }}>
      {/* Background pattern */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${zelijPattern})`,
          backgroundRepeat: "repeat",
          backgroundSize: "400px",
          backgroundAttachment: "fixed",
          opacity: 0.06,
        }}
      />

      {/* ===== HERO SECTION with full-width image ===== */}
      <div className="relative h-[70vh] min-h-[500px] overflow-hidden">
        {/* Hero Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://res.cloudinary.com/dzszitsgb/image/upload/v1773759769/nouzha/sahara-20260317-new.jpg"
            alt="Morocco Travel Services"
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 40%" }}
          />
          {/* Gradient Overlays */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FFF7ED] via-transparent to-transparent" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-5 py-2 rounded-full text-sm font-semibold mb-6 backdrop-blur-md bg-white/20 text-white border border-white/30">
              {safe(t.servicesTitle, "✦ Nos Services Premium")}
            </span>

            <h1 className="text-4xl md:text-6xl font-serif font-extrabold mb-6 tracking-tight text-white drop-shadow-2xl">
              {safe(t.servicesHeroTitlePrefix, "Explorez les")} <span className="text-orange-300">{safe(t.servicesHeroTitleHighlight, "Merveilles")}</span> {safe(t.servicesHeroTitleSuffix, "du Maroc")}
            </h1>

            <p className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed text-white/90 drop-shadow-lg">
              {safe(t.servicesSubtitle, "Découvrez nos services premium pour planifier votre voyage au Maroc en toute simplicité.")}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
              <Link
                to="/circuit"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold shadow-xl hover:from-orange-600 hover:to-red-700 transition-all hover:scale-105 duration-300"
              >
                {safe(t.servicesViewCircuitsCta, "Voir les circuits")}
                <ChevronRight className="w-4 h-4" />
              </Link>
              <Link
                to="/circuit-sur-mesure"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl border border-white/40 text-white backdrop-blur-md bg-white/10 font-semibold hover:bg-white/20 transition-all"
              >
                {safe(t.servicesCustomRequestCta, "Demande sur mesure")}
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Animated floating elements */}
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, -20, 0],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 4 + i,
              repeat: Infinity,
              delay: i * 0.7,
              ease: "easeInOut",
            }}
            className="absolute w-2 h-2 rounded-full bg-orange-400/60"
            style={{
              bottom: `${20 + i * 8}%`,
              left: `${10 + i * 18}%`,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-6 max-w-7xl relative z-10" style={{ marginTop: "-2rem" }}>
        {/* Trust strip with icons */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
          {trustHighlights.map((item, i) => (
            <motion.article
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="rounded-2xl border border-amber-200/80 bg-white/95 backdrop-blur-sm p-6 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center gap-2 mb-2">
                {item.icon}
                <span className="text-xs uppercase tracking-wide text-amber-700/80 font-bold">{item.title}</span>
              </div>
              <div className="text-2xl font-extrabold text-amber-900">{item.value}</div>
              <p className="mt-2 text-sm text-amber-900/70 leading-relaxed">{item.description}</p>
            </motion.article>
          ))}
        </section>

        {/* ===== SERVICES GRID with Images ===== */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-5 py-2 rounded-full text-sm font-semibold mb-4 border backdrop-blur-md border-orange-400/30 bg-amber-100/50 text-amber-700">
              {safe(t.servicesWhatWeOffer, "Ce que nous proposons")}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-extrabold tracking-tight text-amber-900">
              Nos <span className="text-orange-600">Services</span>
            </h2>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-24">
          {services.map((service, idx) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
              whileHover={{ y: -8 }}
              className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-amber-100/50"
            >
              {/* Service Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={serviceImages[service.id]}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-xl p-2.5 shadow-lg">
                  <span className="text-orange-600">{service.icon}</span>
                </div>
              </div>

              {/* Service Content */}
              <div className="p-6">
                <h3 className="text-xl font-serif font-bold mb-2 text-amber-900 group-hover:text-orange-700 transition-colors">
                  {service.title}
                </h3>
                <p className="text-sm leading-relaxed text-amber-900/70 mb-4">
                  {service.description}
                </p>
                <Link
                  to={service.to}
                  className="inline-flex items-center text-orange-600 font-semibold group-hover:gap-2 transition-all"
                >
                  {safe(t.explore, "Explorer")}
                  <ChevronRight className="ml-1 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* ===== DESTINATIONS GALLERY STRIP ===== */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-24"
        >
          <div className="text-center mb-10">
            <span className="inline-block px-5 py-2 rounded-full text-sm font-semibold mb-4 border backdrop-blur-md border-orange-400/30 bg-amber-100/50 text-amber-700">
              {safe(t.servicesPopularDestinationsBadge, "Destinations populaires")}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-extrabold tracking-tight text-amber-900">
              {safe(t.servicesDiscoverTitlePrefix, "Découvrez le")} <span className="text-orange-600">{safe(t.servicesDiscoverTitleHighlight, "Maroc")}</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {galleryImages.map((img, i) => (
              <motion.div
                key={img.label}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={{ scale: 1.05 }}
                className="relative rounded-2xl overflow-hidden shadow-lg group cursor-pointer"
                style={{ aspectRatio: i === 0 ? "1/1.2" : "1/1.2" }}
              >
                <img
                  src={img.src}
                  alt={img.label}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <p className="text-white font-bold text-sm drop-shadow-lg">{img.label}</p>
                </div>
                {/* Shimmer overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* ===== PROCESS SECTION with images ===== */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-24"
        >
          <div className="text-center mb-16">
            <span className="inline-block px-5 py-2 rounded-full text-sm font-semibold mb-4 border backdrop-blur-md border-orange-400/30 bg-amber-100/50 text-amber-700">
              {safe(t.processTitle, "Comment ça marche")}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-extrabold tracking-tight text-amber-900">
              {safe(t.servicesJourneyTitlePrefix, "Votre")} <span className="text-orange-600">{safe(t.servicesJourneyTitleHighlight, "Parcours")}</span> {safe(t.servicesJourneyTitleSuffix, "avec Nouzha")}
            </h2>
          </div>

          <div className="space-y-12">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -60 : 60 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`flex flex-col ${index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"} items-center gap-8`}
              >
                {/* Image Side */}
                <div className="md:w-1/2 relative">
                  <div className="rounded-3xl overflow-hidden shadow-2xl relative group">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-64 md:h-72 object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-orange-900/40 via-transparent to-transparent" />
                    {/* Step number badge */}
                    <div className="absolute top-4 left-4 w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-xl">
                      <span className="text-white font-bold text-lg">{index + 1}</span>
                    </div>
                  </div>
                </div>

                {/* Content Side */}
                <div className="md:w-1/2">
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-amber-100/50">
                    <div className="inline-flex items-center gap-2 bg-orange-50 rounded-full px-4 py-2 mb-4">
                      {step.icon}
                      <span className="text-xs font-bold uppercase tracking-wider text-orange-700">
                        {safe(t.servicesStepLabel, "Étape")} {index + 1}
                      </span>
                    </div>
                    <h3 className="text-2xl font-serif font-bold mb-3 text-amber-900">
                      {step.title}
                    </h3>
                    <p className="leading-relaxed text-amber-900/70">{step.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* ===== STATS / SOCIAL PROOF ===== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src="https://res.cloudinary.com/dzszitsgb/image/upload/v1773766008/nouzha/destination-chefchaoun-20260317.jpg"
              alt="Morocco panoramic"
              className="w-full h-64 md:h-80 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-orange-900/80 via-orange-800/70 to-red-900/80" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-16 text-center text-white">
                {[
                  { label: safe(t.servicesStatsClientsLabel, "Clients satisfaits"), value: "2000+" },
                  { label: safe(t.servicesStatsCircuitsLabel, "Circuits proposés"), value: "120+" },
                  { label: safe(t.servicesStatsCitiesLabel, "Villes couvertes"), value: "30+" },
                  { label: safe(t.servicesStatsRatingLabel, "Note moyenne"), value: "4.9", icon: <Star className="w-4 h-4 text-yellow-400 inline ml-1" /> },
                ].map((stat, i) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="text-3xl md:text-4xl font-extrabold">
                      {stat.value}
                      {stat.icon}
                    </div>
                    <div className="text-sm text-white/80 mt-1">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* ===== CTA Section with background image ===== */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <div className="relative rounded-3xl overflow-hidden shadow-2xl">
            <img
              src="https://res.cloudinary.com/dzszitsgb/image/upload/v1773759766/nouzha/fes-20260317-new.jpg"
              alt="Morocco cityscape"
              className="w-full h-80 md:h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-orange-900/85 to-red-900/70" />

            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center px-6 max-w-3xl">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <h2 className="text-3xl md:text-5xl font-serif font-bold mb-4 text-white">
                    {safe(t.ctaTitle, "Prêt à partir ?")}
                  </h2>
                  <p className="max-w-2xl mx-auto mb-8 text-lg text-white/90">
                    {safe(t.ctaSubtitle, "Contactez-nous et nous vous aiderons à planifier l'expérience parfaite.")}
                  </p>

                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Link
                      to="/contact"
                      className="inline-flex items-center px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl bg-white text-orange-700 hover:bg-orange-50"
                    >
                      {safe(t.startBooking, "Commencer ma réservation")}
                      <ChevronRight className="ml-2 w-5 h-5" />
                    </Link>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Services;
